import { Avatar, type AvatarColor } from "@elion-health/ui";
import {
  COVERAGE_EDGE_CLASS,
  COVERAGE_LABEL,
  type CoverageState,
} from "./coverage";
import type { MapLinkComponent } from "./link";

export interface StepCardProduct {
  productId: string;
  name: string;
  vendorName: string | null;
}

export interface StepCardApplication {
  id: string;
  name: string;
  vendor: string | null;
  products: StepCardProduct[];
}

export interface StepCardStep<S extends string = CoverageState> {
  position: number;
  name: string;
  definition?: string | null;
  coverage: S;
  applications: StepCardApplication[];
}

/** How a card edges and names its state. */
export interface StepCardPalette<S extends string> {
  edge: Record<S, string>;
  label: Record<S, string>;
}

const COVERAGE_CARD_PALETTE: StepCardPalette<CoverageState> = {
  edge: COVERAGE_EDGE_CLASS,
  label: COVERAGE_LABEL,
};

export interface StepCardProps<S extends string = CoverageState> {
  step: StepCardStep<S>;
  /** The state's edge color and name; the map's by default. */
  palette?: StepCardPalette<S>;
  /** The step currently scrolled to. */
  active?: boolean;
  /** Makes each application row a link, e.g. to the application's page. */
  applicationHref?: (applicationId: string) => string;
  linkComponent?: MapLinkComponent;
}

/**
 * One workflow step in full: its position, name, and definition, with the
 * applications mapped to it. A colored left edge carries the step's coverage.
 */
export function StepCard<S extends string = CoverageState>({
  step,
  palette = COVERAGE_CARD_PALETTE as unknown as StepCardPalette<S>,
  active = false,
  applicationHref,
  linkComponent,
}: StepCardProps<S>) {
  const { applications } = step;
  return (
    <article
      aria-label={`Step ${step.position}: ${step.name}. ${palette.label[step.coverage]}.`}
      className={`rounded-(--radius-container) border border-l-3 border-(--border-subtle) p-4 transition-colors ${
        palette.edge[step.coverage]
      } ${active ? "bg-(--accent-cobalt-bg)/40" : "bg-(--bg-white)"}`}
    >
      <h2 className="flex items-baseline gap-2 text-[13.5px]! font-semibold">
        <span className="tabular-nums text-(--fg-muted)">{step.position}</span>
        {step.name}
      </h2>
      {step.definition && (
        <p className="mt-1! max-w-3xl text-[12px] leading-relaxed text-(--fg-muted)!">
          {step.definition}
        </p>
      )}
      <p className="mt-3! mb-2.5! text-[10px] font-semibold text-(--fg-muted)!">
        {applications.length === 1 ? "Application" : "Applications"}
      </p>
      {applications.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {applications.map((app) => (
            <li key={app.id}>
              <ApplicationRow
                app={app}
                href={applicationHref?.(app.id)}
                linkComponent={linkComponent}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11.5px] text-(--fg-muted)!">
          No application mapped
        </p>
      )}
    </article>
  );
}

const AVATAR_COLORS: AvatarColor[] = ["teal", "lime", "clay", "stone", "navy"];

/** A stable avatar color per application, so it reads the same everywhere. */
function avatarColor(id: string): AvatarColor {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length] ?? "navy";
}

/**
 * An application on a step: who makes it and which catalog products it is.
 * With an `href`, the whole row opens it.
 */
function ApplicationRow({
  app,
  href,
  linkComponent: Link = "a",
}: {
  app: StepCardApplication;
  href?: string;
  linkComponent?: MapLinkComponent;
}) {

  return (
    <div
      className={`relative flex items-center gap-2.5 rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white) p-3 ${
        href
          ? "transition-colors focus-within:border-(--border-strong) hover:border-(--border-strong)"
          : ""
      }`}
    >
      <span aria-hidden={true} className="flex">
        <Avatar
          name={app.name}
          size="xs"
          color={avatarColor(app.id)}
          className="size-7 rounded-(--radius-field) text-[10.5px]"
        />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] font-semibold text-(--fg-primary)!">
          {href ? (
            // Stretched link: the whole row opens the application.
            <Link
              href={href}
              className="text-inherit! no-underline! after:absolute after:inset-0 after:rounded-(--radius-container) focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-(--fg-primary)"
            >
              {app.name}
            </Link>
          ) : (
            app.name
          )}
          {app.vendor && (
            <span className="font-normal text-(--fg-muted)">
              {" "}
              · {app.vendor}
            </span>
          )}
        </p>
        {app.products.length > 0 && (
          <p className="mt-0.5! truncate text-[11px] text-(--fg-secondary)!">
            {app.products.length === 1 ? "Product" : "Products"}:{" "}
            {app.products
              .map((product) =>
                product.vendorName
                  ? `${product.name} (${product.vendorName})`
                  : product.name,
              )
              .join(", ")}
          </p>
        )}
      </div>
      {href && (
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden={true}
          className="shrink-0 text-(--fg-subtle)"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      )}
    </div>
  );
}
